import React, { useContext, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"

import GlobalState from "contexts/GlobalState"
import Breadcrumbs from "components/Common/Breadcrumb"
import QuickGuide from "components/QuickGuides/QuickGuide"

import DeleteGroupModal from "./Components/DeleteVideoGroupModal"
import VideoGroupModal from "./Components/VideoGroupModal"
import VideoGroupUsersModal from "./Components/VideoGroupUsersModal"

import EventsContent from "./Content/EventsContent"
import VirtualRoomsContent from "./Content/VirtualRoomsContent"
import ConversationsContent from "./Content/ConversationsContent"
import RecordingsContent from "./Content/RecordingsContent"

import {
  acGetVirtualRoomList,
  getACsGroupsTree,
  getVideoCalendarEventsByGroup,
  acGetRecordings,
} from "helpers/backend_helper"
import {
  getLottieLoadingOptions,
  getLottieNothingFound,
  getLottieSearch,
  showToast,
} from "utils/general"

import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Alert,
  Button,
  Card,
  CardBody,
  CardSubtitle,
  CardTitle,
  Col,
  Container,
  Row,
} from "reactstrap"
import { Switch, Tree, Tooltip } from "antd"

import Lottie from "react-lottie"
import classNames from "classnames"
import dayjs from "dayjs"

const VideoContentEvents = props => {
  const [state] = useContext(GlobalState)
  const [quickGuide, setQuickGuide] = useState(false)
  const [isSystemAdmin, setIsSystemAdmin] = useState(false)
  const [isProduction, setIsProduction] = useState(true)
  const [ongoingAction, setOngoingAction] = useState(false)

  const [treeData, setTreeData] = useState([])
  const [loadingTree, setLoadingTree] = useState(false)
  const [selectedTreeOption, setSelectedTreeOption] = useState()
  const [selectedNode, setSelectedNode] = useState([])
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false)
  const [showUsersModal, setShowUsersModal] = useState(false)
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [groupEditData, setGroupEditData] = useState(null)

  const [accordionOpen1, setAccordionOpen1] = useState("")
  const [accordionOpen2, setAccordionOpen2] = useState("")
  const [accordionOpen3, setAccordionOpen3] = useState("")
  const [accordionOpen4, setAccordionOpen4] = useState("")

  // Events
  const [events, setEvents] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [eventsLoaded, setEventsLoaded] = useState(false)

  // Virtual rooms
  const [virtualRooms, setVirtualRooms] = useState([])
  const [loadingVirtualRooms, setLoadingVirtualRooms] = useState(false)
  const [virtualRoomsLoaded, setVirtualRoomsLoaded] = useState(false)
  const [virtualRoomsModal, setVirtualRoomsModal] = useState(false)
  const [deleteRoomModal, setDeleteRoomModal] = useState(false)

  // Conversations
  const [conversations, setConversations] = useState([])
  const [loadingConversations, setLoadingConversations] = useState(false)
  const [conversationsLoaded, setConversationsLoaded] = useState(false)

  // Recordings
  const [recordings, setRecordings] = useState([])
  const [loadingRecordings, setLoadingRecordings] = useState(false)
  const [recordingsLoaded, setRecordingsLoaded] = useState(false)
  const [selectedRoomId, setSelectedRoomId] = useState(null)
  const [selectedRoom, setSelectedRoom] = useState(null)

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = props.t("Content events") + " | Suvanto Care"
    abortRef.current = new AbortController()
    if (state.authUser != null) {
      const obj = JSON.parse(state.authUser)
      let role = obj.activeRole ? obj.activeRole : obj.role
      if (role === "system_admin") {
        setIsSystemAdmin(true)
      }
    }
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    getGroups()

    setSelectedTreeOption()
    setSelectedNode()

    setEvents([])
    setVirtualRooms([])
    setConversations([])
    setRecordings([])

    setEventsLoaded(false)
    setVirtualRoomsLoaded(false)
    setConversationsLoaded(false)
    setRecordingsLoaded(false)

    setAccordionOpen1("")
    setAccordionOpen2("")
    setAccordionOpen3("")
    setAccordionOpen4("")
  }, [isProduction])

  // Load groups
  const getGroups = () => {
    if (!loadingTree) {
      setTreeData([])
      setSelectedNode()
      setSelectedTreeOption()
      //setSelectedValue(null)
      setLoadingTree(true)
      getACsGroupsTree(abortRef.current, isProduction)
        .then(result => {
          if (result.statusCode === 200 && result.groups != null) {
            //console.log("Tree: ", result.groups)
            setTreeData(result.groups)
          } else throw new Error("Unexpected result")
        })
        .catch(err => {
          console.log(err)
          //setTreeData([])
          //setSelectedNode()
          //setSelectedTreeOption()
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setLoadingTree(false)
        })
    }
  }

  // Load events
  const getEvents = groupId => {
    if (!loadingEvents && groupId) {
      setLoadingEvents(true)
      getVideoCalendarEventsByGroup(abortRef.current, groupId, isProduction)
        .then(result => {
          if (result.statusCode === 200 && result.events != null) {
            let temp = []
            //console.log("Raw events: ", result.events)
            for (const element of result.events) {
              let end = element.start + element.duration * 60 * 1000
              let entry = new Object()
              entry.id = element.id
              entry.title = element.title
              entry.room = element.roomName
              entry.vmr_id = element.vmr_id
              entry.date = dayjs(element.start).format("DD.MM")
              entry.time =
                dayjs(element.start).format("HH:mm") +
                "-" +
                dayjs(end).format("HH:mm")
              entry.canceled = element.canceled
              entry.duration = element.duration
              entry.start = element.start
              entry.image_base64 = element.image_base64
              entry.recursion_serie = element.recursion_serie
              temp.push(entry)
            }
            setEvents(temp)
            setEventsLoaded(true)
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setLoadingEvents(false)
        })
    }
  }

  // Load virtual rooms
  const getVirtualRooms = groupId => {
    if (!loadingVirtualRooms && groupId) {
      setLoadingVirtualRooms(true)
      acGetVirtualRoomList(abortRef.current, groupId, 1, isProduction)
        .then(result => {
          //console.log("Virtual rooms result: ", result)
          if (result.statusCode === 200 && result.list != null) {
            setVirtualRooms(result.list)
            setVirtualRoomsLoaded(true)
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setLoadingVirtualRooms(false)
        })
    }
  }

  const getConversations = groupId => {
    if (!loadingConversations && groupId) {
      setLoadingConversations(true)
      acGetVirtualRoomList(abortRef.current, groupId, 2, isProduction)
        .then(result => {
          if (result.statusCode === 200 && result.list != null) {
            //console.log("Community rooms: ", result.list)
            setConversations(result.list)
            setConversationsLoaded(true)
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setLoadingConversations(false)
        })
    }
  }

  const getRecordings = groupId => {
    if (!loadingRecordings && groupId) {
      setLoadingRecordings(true)
      acGetRecordings(abortRef.current, groupId, isProduction)
        .then(result => {
          if (result.statusCode === 200 && result.list != null) {
            //console.log("Recordings: ", result.list)
            setRecordings(result.list)
            setRecordingsLoaded(true)
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setLoadingRecordings(false)
        })
    }
  }

  // New room validation init
  const setNewRoom = type => {
    let newEntry = new Object()
    newEntry.id = undefined
    newEntry.type = type
    newEntry.name = ""
    newEntry.requiresHost = false
    newEntry.opensAt = "10:00:00"
    newEntry.closesAt = "14:00:00"
    newEntry.imageBase64 = ""

    setSelectedRoom(newEntry)
    setVirtualRoomsModal(true)
  }

  // Tree selection changed
  const onSelect = (selection, extras) => {
    //console.log("Group: ", selection[0])
    setSelectedTreeOption(selection[0])
    setSelectedNode(extras.node)
    setVirtualRooms([])
    setConversations([])

    setEventsLoaded(false)
    setVirtualRoomsLoaded(false)
    setConversationsLoaded(false)
    setRecordingsLoaded(false)

    if (selection.length > 0) {
      if (accordionOpen1) getEvents(selection[0])
      if (accordionOpen2) getVirtualRooms(selection[0])
      if (accordionOpen3) getConversations(selection[0])
      if (accordionOpen4) getRecordings(selection[0])
    }
  }

  // Accordion toggle
  const toggleInfo = id => {
    if (id == 1) {
      if (accordionOpen1 === id) {
        setAccordionOpen1("")
      } else {
        setAccordionOpen1(id)
        if (!eventsLoaded) getEvents(selectedTreeOption)
      }
    }
    if (id == 2) {
      if (accordionOpen2 === id) {
        setAccordionOpen2("")
      } else {
        setAccordionOpen2(id)
        if (!virtualRoomsLoaded) getVirtualRooms(selectedTreeOption)
      }
    }
    if (id == 3) {
      if (accordionOpen3 === id) {
        setAccordionOpen3("")
      } else {
        setAccordionOpen3(id)
        if (!conversationsLoaded) getConversations(selectedTreeOption)
      }
    }
    if (id == 4) {
      if (accordionOpen4 === id) {
        setAccordionOpen4("")
      } else {
        setAccordionOpen4(id)
        if (!recordingsLoaded) getRecordings(selectedTreeOption)
      }
    }
  }

  // Get group type description
  const getGroupType = id => {
    switch (id) {
      case "0":
        return "Undefined"
      case "1":
        return "Company"
      case "5":
        return "Regular group"
      case "9":
        return "Homecare users"
      case "10":
        return "Family"
      case "11":
        return "Family members"
      case "15":
        return "Visibility list"
      case "16":
        return "Professionals"
      case "17":
        return "Service center staff"
      case "19":
        return "Visibility list for professionals"
      case "20":
        return "Visibility list for homecare users"
      case "21":
        return "Visibility list for family members"
      case "22":
        return "Visibility list for guests"
      case "25":
        return "Content visibility list"
      default:
        return "" + id
    }
  }

  // Get group type content
  const checkGroupType = id => {
    switch (id) {
      case "1":
      case "5":
      case "10":
      case "11":
      case "16":
      case "19":
      case "20":
      case "21":
      case "22":
        return true
      default:
        return false
    }
  }

  return (
    <>
      <div className="page-content">
        <Container fluid className={quickGuide ? "quick-guide-open" : ""}>
          <Breadcrumbs
            title={props.t("Content events")}
            className="breadcrumb m-0 justify-content-center"
            breadcrumbItem={
              <div className="d-inline-block">
                <QuickGuide
                  source={"videoContent"}
                  title="Content events"
                  isOpen={quickGuide}
                  setIsOpen={setQuickGuide}
                />
              </div>
            }
          />
          <Row>
            <Col lg={quickGuide ? 12 : 4}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3">
                    {props.t("Groups")}
                    {isSystemAdmin && (
                      <Switch
                        size="small"
                        disabled={
                          loadingTree ||
                          ongoingAction ||
                          loadingEvents ||
                          loadingVirtualRooms ||
                          loadingConversations ||
                          loadingRecordings
                        }
                        checkedChildren="PROD"
                        unCheckedChildren="DEMO"
                        className={"mt-1 float-end"}
                        checked={isProduction}
                        onChange={v => setIsProduction(v)}
                      ></Switch>
                    )}
                  </CardTitle>
                  {loadingTree && (
                    <>
                      <div className="text-center">
                        <Lottie
                          options={getLottieLoadingOptions()}
                          height={100}
                          width={100}
                        />
                      </div>
                    </>
                  )}
                  {!loadingTree && treeData.length == 0 && (
                    <div className="text-center">
                      <Lottie
                        options={getLottieNothingFound()}
                        height={100}
                        width={100}
                      />
                    </div>
                  )}
                  {!loadingTree && treeData.length > 0 && (
                    <div
                      className="custom-scrollbar content-group-tree"
                      style={{
                        maxHeight: quickGuide ? "30vh" : "65vh",
                      }}
                    >
                      <Tree
                        showLine
                        disabled={
                          loadingTree ||
                          ongoingAction ||
                          loadingEvents ||
                          loadingConversations ||
                          loadingRecordings ||
                          loadingVirtualRooms
                        }
                        defaultExpandAll={true}
                        autoExpandParent={true}
                        onSelect={onSelect}
                        treeData={treeData}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={quickGuide ? 12 : 8}>
              {selectedTreeOption != undefined ? (
                <>
                  <Row>
                    <Col>
                      <Card>
                        <CardBody>
                          <CardTitle>
                            <Row>
                              <Col>{selectedNode?.title}</Col>
                              {isSystemAdmin && (
                                <Col className="col-auto">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    disabled={loadingTree}
                                    onClick={() => {
                                      setShowUsersModal(true)
                                    }}
                                  >
                                    <i className="me-2 bx bxs-group text-truncate align-middle"></i>
                                    <span className="">
                                      {props.t("Group members")}
                                    </span>
                                  </Button>
                                  <Button
                                    color="success"
                                    size="sm"
                                    disabled={loadingTree}
                                    className="ms-2"
                                    onClick={() => {
                                      let newGroup = {
                                        parent_id: selectedNode?.value,
                                      }
                                      setGroupEditData(newGroup)
                                      setShowGroupModal(true)
                                    }}
                                  >
                                    <i className="me-2 bx bxs-folder-plus text-truncate align-middle"></i>
                                    <span className="">
                                      {props.t("Add subgroup")}
                                    </span>
                                  </Button>

                                  <Tooltip
                                    title={props.t("Edit group")}
                                    placement="top"
                                    trigger={["hover", "focus"]}
                                  >
                                    <Button
                                      color="primary"
                                      id="edit-group-btn"
                                      size="sm"
                                      disabled={loadingTree}
                                      className="ms-2"
                                      onClick={() => {
                                        let editGroup = {
                                          parent_id: selectedNode?.parent_id,
                                          group_id: selectedNode?.value,
                                          name: selectedNode?.title,
                                          group_type: selectedNode?.group_type,
                                        }
                                        setGroupEditData(editGroup)
                                        setShowGroupModal(true)
                                      }}
                                    >
                                      <i className="bx bxs-edit text-truncate align-middle"></i>
                                    </Button>
                                  </Tooltip>

                                  <Tooltip
                                    title={props.t("Delete group")}
                                    placement="top"
                                    trigger={["hover", "focus"]}
                                  >
                                    <Button
                                      id="delete-group-btn"
                                      color="danger"
                                      size="sm"
                                      disabled={loadingTree}
                                      className="ms-2"
                                      onClick={() => {
                                        setShowDeleteGroupModal(true)
                                      }}
                                    >
                                      <i className="bx bxs-trash text-truncate align-middle"></i>
                                    </Button>
                                  </Tooltip>
                                </Col>
                              )}
                            </Row>
                          </CardTitle>
                          <CardSubtitle>
                            {props.t(getGroupType(selectedNode?.group_type))}
                            {isSystemAdmin && (
                              <span>, id: {selectedNode?.value}</span>
                            )}
                          </CardSubtitle>
                        </CardBody>
                        {checkGroupType(selectedNode?.group_type) ? (
                          <>
                            <CardBody className="pt-0 ">
                              <Alert color="info">
                                {props.t("No content")}
                              </Alert>
                            </CardBody>
                          </>
                        ) : (
                          <>
                            <CardBody className="p-0">
                              <Accordion
                                flush
                                open={accordionOpen1}
                                toggle={toggleInfo}
                              >
                                <AccordionItem>
                                  <AccordionHeader targetId="1">
                                    {props.t("Events")}
                                  </AccordionHeader>
                                  <AccordionBody
                                    accordionId="1"
                                    className="pt-0"
                                    style={{ paddingTop: "0px" }}
                                  >
                                    <EventsContent
                                      events={events}
                                      isProduction={isProduction}
                                      ongoingAction={ongoingAction}
                                      setOngoingAction={setOngoingAction}
                                      loadingEvents={loadingEvents}
                                      setLoadingEvents={setLoadingEvents}
                                      getEvents={getEvents}
                                      eventsLoaded={eventsLoaded}
                                      selectedTreeOption={selectedTreeOption}
                                      virtualRooms={virtualRooms}
                                      getVirtualRooms={getVirtualRooms}
                                    />
                                  </AccordionBody>
                                </AccordionItem>
                              </Accordion>
                            </CardBody>
                            <CardBody className="p-0">
                              <Accordion
                                flush
                                open={accordionOpen2}
                                toggle={toggleInfo}
                              >
                                <AccordionItem>
                                  <AccordionHeader targetId="2">
                                    {props.t("Virtual rooms")}
                                  </AccordionHeader>
                                  <AccordionBody
                                    accordionId="2"
                                    className="pt-0"
                                    style={{ paddingTop: "0px" }}
                                  >
                                    <VirtualRoomsContent
                                      isProduction={isProduction}
                                      virtualRooms={virtualRooms}
                                      ongoingAction={ongoingAction}
                                      setOngoingAction={setOngoingAction}
                                      loadingVirtualRooms={loadingVirtualRooms}
                                      virtualRoomsModal={virtualRoomsModal}
                                      selectedRoom={selectedRoom}
                                      setSelectedRoom={setSelectedRoom}
                                      deleteRoomModal={deleteRoomModal}
                                      getVirtualRooms={getVirtualRooms}
                                      selectedRoomId={selectedRoomId}
                                      setSelectedRoomId={setSelectedRoomId}
                                      setNewRoom={setNewRoom}
                                      setVirtualRoomsModal={
                                        setVirtualRoomsModal
                                      }
                                      setDeleteRoomModal={setDeleteRoomModal}
                                      selectedTreeOption={selectedTreeOption}
                                      getConversations={getConversations}
                                    />
                                  </AccordionBody>
                                </AccordionItem>
                              </Accordion>
                            </CardBody>
                            <CardBody className="p-0">
                              <Accordion
                                flush
                                open={accordionOpen3}
                                toggle={toggleInfo}
                              >
                                <AccordionItem>
                                  <AccordionHeader targetId="3">
                                    {props.t("Conversations")}
                                  </AccordionHeader>
                                  <AccordionBody
                                    accordionId="3"
                                    className="pt-0"
                                    style={{ paddingTop: "0px" }}
                                  >
                                    <ConversationsContent
                                      conversations={conversations}
                                      isProduction={isProduction}
                                      loadingConversations={
                                        loadingConversations
                                      }
                                      setLoadingConversations={
                                        setLoadingConversations
                                      }
                                      setConversations={setConversations}
                                      setNewRoom={setNewRoom}
                                      setSelectedRoom={setSelectedRoom}
                                      setVirtualRoomsModal={
                                        setVirtualRoomsModal
                                      }
                                      setSelectedRoomId={setSelectedRoomId}
                                      setDeleteRoomModal={setDeleteRoomModal}
                                    />
                                  </AccordionBody>
                                </AccordionItem>
                              </Accordion>
                            </CardBody>
                            <CardBody className="p-0 mb-3">
                              <Accordion
                                flush
                                open={accordionOpen4}
                                toggle={toggleInfo}
                              >
                                <AccordionItem>
                                  <AccordionHeader targetId="4">
                                    {props.t("Recordings")}
                                  </AccordionHeader>
                                  <AccordionBody
                                    accordionId="4"
                                    className="pt-0"
                                    style={{ paddingTop: "0px" }}
                                  >
                                    <RecordingsContent
                                      recordings={recordings}
                                      getRecordings={getRecordings}
                                      isProduction={isProduction}
                                      ongoingAction={ongoingAction}
                                      setOngoingAction={setOngoingAction}
                                      loadingRecordings={loadingRecordings}
                                      setLoadingRecordings={
                                        setLoadingRecordings
                                      }
                                      setRecordingsLoaded={setRecordingsLoaded}
                                      recordingsLoaded={recordingsLoaded}
                                      selectedTreeOption={selectedTreeOption}
                                    />
                                  </AccordionBody>
                                </AccordionItem>
                              </Accordion>
                            </CardBody>
                          </>
                        )}
                      </Card>
                    </Col>
                  </Row>
                </>
              ) : (
                <Card>
                  <CardBody>
                    <div className="text-center">
                      <Lottie
                        options={getLottieSearch()}
                        height={150}
                        width={150}
                      />
                    </div>
                    <div className="text-center">
                      <h4 className="text-muted mb-4">
                        {props.t("Select group")}
                      </h4>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <DeleteGroupModal
        showModal={showDeleteGroupModal}
        setShowModal={setShowDeleteGroupModal}
        groupId={selectedNode?.value}
        groupName={selectedNode?.title}
        production={isProduction}
        refreshGroups={getGroups}
      />

      <VideoGroupModal
        showModal={showGroupModal}
        setShowModal={setShowGroupModal}
        groupData={groupEditData}
        production={isProduction}
        refreshGroups={getGroups}
      />

      <VideoGroupUsersModal
        showModal={showUsersModal}
        setShowModal={setShowUsersModal}
        groupId={selectedNode?.value}
        groupName={selectedNode?.title}
        production={isProduction}
      />
    </>
  )
}

VideoContentEvents.propTypes = {
  t: PropTypes.any,
}
export default withTranslation()(VideoContentEvents)
